/**
 * VH-253 — the shopping list, built for a phone in a shop.
 *
 * One column, 44px+ targets, no tabs and no menus: open items by aisle in walking
 * order, then what was ticked off. Everything that fills the list (type, paste, a
 * recipe, this week's plan) sits above the list, and the list itself is nothing but
 * tappable rows.
 *
 * Three states earn their own copy rather than a blank page or a spinner-forever:
 * an empty list explains the ways to fill it, a failed tick says the save did
 * not happen, and a paste that recognised nothing keeps the text (PasteImport).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingCartIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'

import AddItem from '../components/shopping/AddItem'
import AisleSection from '../components/shopping/AisleSection'
import PasteImport from '../components/shopping/PasteImport'
import RecipePicker from '../components/shopping/RecipePicker'
import ShoppingRow from '../components/shopping/ShoppingRow'
import { errorCode } from '../services/shopping'
import {
  useAddFromPlan,
  useAddFromRecipe,
  useAddItem,
  useClearBought,
  useImportPaste,
  useRemoveItem,
  useSetBought,
  useShoppingList,
} from '../hooks/useShoppingList'

export default function Shopping() {
  const { t } = useTranslation()
  const { data, isLoading, error } = useShoppingList()

  const setBought = useSetBought()
  const addItem = useAddItem()
  const importPaste = useImportPaste()
  const addFromPlan = useAddFromPlan()
  const addFromRecipe = useAddFromRecipe()
  const [addingRecipeId, setAddingRecipeId] = useState(null)
  const removeItem = useRemoveItem()
  const clearBought = useClearBought()

  // One line of feedback for the whole screen: a failed tick, a plan with no document,
  // a successful top-up. More than one at a time would be noise on a 375px screen.
  const [message, setMessage] = useState(null)

  const groups = data?.groups || []
  const bought = data?.bought || []
  const openCount = data?.open_count || 0
  const isEmpty = !isLoading && !error && openCount === 0 && bought.length === 0

  const handleToggle = async (item, next) => {
    setMessage(null)
    try {
      await setBought.mutateAsync({ id: item.id, bought: next })
    } catch {
      // The hook has already put the row back where it was.
      setMessage({ tone: 'warn', text: t('shopping.errors.saveFailed') })
    }
  }

  const handleAdd = async (name) => {
    setMessage(null)
    try {
      await addItem.mutateAsync({ name })
      return true
    } catch {
      setMessage({ tone: 'warn', text: t('shopping.errors.addFailed') })
      return false
    }
  }

  const handlePaste = async (text) => {
    setMessage(null)
    try {
      const result = await importPaste.mutateAsync(text)
      return { status: 'ok', ...result }
    } catch (err) {
      return { status: errorCode(err) === 'nothing_recognised' ? 'nothing' : 'error' }
    }
  }

  const handleFromPlan = async () => {
    setMessage(null)
    try {
      const result = await addFromPlan.mutateAsync()
      setMessage({
        tone: 'ok',
        text: t('shopping.plan.added', {
          added: result.added_count ?? 0,
          merged: result.merged_count ?? 0,
        }),
      })
    } catch (err) {
      const code = errorCode(err)
      setMessage({
        tone: 'warn',
        text:
          code === 'no_plan_document'
            ? t('shopping.plan.noPlan')
            : code === 'plan_has_no_shopping_lines'
              ? t('shopping.plan.noLines')
              : t('shopping.plan.failed'),
      })
    }
  }

  // VH-274: the current version of the recipe, as the recipe page's own button sends it.
  const handleFromRecipe = async (recipe) => {
    setMessage(null)
    setAddingRecipeId(recipe.id)
    try {
      const result = await addFromRecipe.mutateAsync({ recipeId: recipe.id })
      setMessage({
        tone: 'ok',
        text: t('shopping.recipe.added', {
          title: recipe.title,
          added: result.added_count ?? 0,
          merged: result.merged_count ?? 0,
        }),
      })
    } catch (err) {
      setMessage({
        tone: 'warn',
        text:
          errorCode(err) === 'recipe_has_no_ingredients'
            ? t('shopping.recipe.noIngredients', { title: recipe.title })
            : t('shopping.recipe.failed', { title: recipe.title }),
      })
    } finally {
      setAddingRecipeId(null)
    }
  }

  const handleRemove = async (item) => {
    setMessage(null)
    try {
      await removeItem.mutateAsync(item.id)
    } catch {
      setMessage({ tone: 'warn', text: t('shopping.errors.removeFailed') })
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-3 py-4 pb-24" data-testid="shopping-page">
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-on-surface">
          <ShoppingCartIcon className="h-7 w-7 text-primary-600" aria-hidden="true" />
          {t('shopping.title')}
        </h1>
        <p className="mt-0.5 text-sm text-on-surface-variant" data-testid="shopping-open-count">
          {t('shopping.openCount', { count: openCount })}
        </p>
      </header>

      <div className="mb-4 space-y-2">
        <AddItem onAdd={handleAdd} pending={addItem.isPending} />
        <PasteImport
          onImport={handlePaste}
          pending={importPaste.isPending}
          defaultOpen={isEmpty}
        />
        <RecipePicker onPick={handleFromRecipe} pendingId={addingRecipeId} />
        <button
          type="button"
          onClick={handleFromPlan}
          disabled={addFromPlan.isPending}
          className="btn-outline flex w-full items-center justify-center gap-2 min-h-[44px]"
          data-testid="shopping-from-plan"
        >
          <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
          {addFromPlan.isPending ? t('shopping.plan.adding') : t('shopping.plan.button')}
        </button>
      </div>

      {message && (
        <p
          role="status"
          className={`mb-3 text-sm ${
            message.tone === 'ok' ? 'text-success-700' : 'text-warning-700'
          }`}
          data-testid="shopping-message"
        >
          {message.text}
        </p>
      )}

      {isLoading && <p className="text-on-surface-variant">{t('common.loading')}</p>}
      {error && (
        <p role="alert" className="text-danger-600" data-testid="shopping-load-error">
          {t('shopping.errors.loadFailed')}
        </p>
      )}

      {isEmpty && (
        <div className="rounded-lg border border-dashed border-outline-variant p-5 text-center" data-testid="shopping-empty">
          <p className="font-medium text-on-surface">{t('shopping.empty.title')}</p>
          <ul className="mt-2 space-y-1 text-sm text-on-surface-variant">
            <li>{t('shopping.empty.paste')}</li>
            <li>{t('shopping.empty.recipe')}</li>
            <li>{t('shopping.empty.plan')}</li>
            <li>{t('shopping.empty.type')}</li>
          </ul>
        </div>
      )}

      {groups.map((group) => (
        <AisleSection
          key={group.group}
          group={group.group}
          items={group.items}
          onToggle={handleToggle}
          onRemove={handleRemove}
        />
      ))}

      {bought.length > 0 && (
        <section className="mt-6" data-testid="shopping-bought">
          <div className="flex items-center justify-between px-3 py-1.5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
              {t('shopping.bought.title', { count: bought.length })}
            </h2>
            <button
              type="button"
              onClick={() => clearBought.mutateAsync().catch(() => {})}
              className="text-xs text-on-surface-variant underline"
              data-testid="shopping-clear-bought"
            >
              {t('shopping.bought.clear')}
            </button>
          </div>
          <ul className="rounded-lg border border-outline-variant bg-surface-container opacity-80">
            {bought.map((item) => (
              <ShoppingRow key={item.id} item={item} bought onToggle={handleToggle} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
